import React, { useRef } from "react";

import { DashboardWindow } from "./dashboardHelpers";
import { useTheme } from "./themes";
import { ErrorStateDetails, useBlockRender } from "./useBlockRender";
import { SlicerState } from "./useDashboardRuntime";

interface Props {
  blockId: string;
  title?: string | null;
  renderFunctionJs: string;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix?: string;
  blockType: string;
  // BE-provided pre-render error attribution (e.g. missing source data).
  errorState?: string | null;
  errorStateDetails?: ErrorStateDetails | null;
  onRendered?: (blockId: string) => void;
  onRenderStart?: (blockId: string) => void;
  onRenderComplete?: (blockId: string) => void;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

/**
 * Free-form block whose entire body is produced by `renderFunctionJs`. The render
 * function receives the card element (see useBlockRender) and typically replaces its
 * innerHTML with custom HTML. Mirrors ChartBlock/DataTableBlock but seeds a plain
 * container (just an optional title) instead of a canvas or grid.
 */
export const CustomHtmlBlock: React.FC<Props> = ({
  blockId,
  title,
  renderFunctionJs,
  slicerState,
  targetWindow,
  idSuffix = "",
  blockType,
  errorState,
  errorStateDetails,
  onRendered,
  onRenderStart,
  onRenderComplete,
  onFixBlock,
  onExplainBlockInChat,
}) => {
  const { themeSignature } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);

  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;

  const escapedTitle = title ? title.replace(/</g, "&lt;").replace(/>/g, "&gt;") : "";

  useBlockRender({
    cardRef,
    // The render function owns the block body and usually replaces innerHTML; the
    // optional title is a sensible starting point for functions that only append.
    initialInnerHtml: escapedTitle ? `<h2 class="themed-card-title">${escapedTitle}</h2>` : "",
    renderFunctionJs,
    slicerState,
    targetWindow,
    fullBlockId,
    blockId,
    blockType,
    blockTitle: title ?? "",
    errorState,
    errorStateDetails,
    themeSignature,
    onRendered,
    onRenderStart,
    onRenderComplete,
    onFixBlock,
    onExplainBlockInChat,
  });

  return <div ref={cardRef} id={fullBlockId} className="themed-dashboard-card" />;
};
